/**
 * Canonical lease-pipeline status logic.
 *
 * Single source of truth for deriving where a booking sits in the leasing
 * pipeline (signature → deposit → active → notice → move-out → closed). Both
 * the Bookings & Tenants overview (AdminTenants) and the Payments hub read from
 * here so "Active" means the same thing on every surface — a payment must never
 * appear for a tenant the tenants list would not call active.
 */

export type LeasePipelineStatus =
  | "signature_pending"
  | "deposit_pending"
  | "active"
  | "notice_given"
  | "move_out_pending"
  | "moved_out"
  | "terminated"
  | "cancelled"
  | "archived"
  | "draft";

// Terminal lifecycle statuses — the deal is closed. These are hidden from the
// default "All statuses" pipeline view and surfaced under their own filter so
// moved-out / terminated / cancelled tenants don't clutter the active pipeline.
export const CLOSED_LEASE_STATUSES: ReadonlySet<LeasePipelineStatus> = new Set([
  "moved_out",
  "terminated",
  "cancelled",
]);

// Explicit lifecycle statuses (set on bookings.status) that carry a definitive
// meaning and should be surfaced verbatim in the overview, overriding the
// contract/deposit-derived pipeline status. Once a deal has moved past pre-sales
// — it's active, closed, or archived — its signature/deposit progress is no
// longer relevant, so we never show "Signature pending"/"Deposit pending" for it.
// Only the truly in-flight statuses (pending, confirmed, approved, signature_pending)
// are omitted here so they keep resolving via contract/deposit progress.
export const LEASE_STATUS_OVERRIDES: Record<string, LeasePipelineStatus> = {
  draft: "draft",
  active: "active",
  notice_given: "notice_given",
  move_out_pending: "move_out_pending",
  moved_out: "moved_out",
  terminated: "terminated",
  cancelled: "cancelled",
  archived: "archived",
};

export function isBookingContractSigned(booking: any): boolean {
  if (!booking) return false;
  if (booking.contract_signed_at) return true;
  const cs = booking.contract_status;
  return cs === "signed" || cs === "approved";
}

/**
 * Broader "contract is signed" check that also counts the manual-handling paths,
 * for surfaces (analytics/occupancy) that must not miss manually-handled tenants:
 *  - DocuSign / portal signature: everything isBookingContractSigned() accepts
 *    (contract_status "signed"/"approved", or contract_signed_at set).
 *  - Manual handling: the admin "manual contract" flag, or an uploaded signed
 *    PDF (signed_contract_url). Manual uploads never set contract_signed_at, so
 *    checking only the DocuSign path would miss every manually-handled tenant.
 * Mirrors computeShowManualContractLabel() used by the tenant pipeline. Kept next
 * to isBookingContractSigned so the pipeline and analytics never drift apart.
 */
export function isBookingContractSignedIncludingManual(
  booking:
    | {
        contract_status?: string | null;
        contract_signed_at?: string | null;
        manual_contract_upload?: boolean | null;
        signed_contract_url?: string | null;
      }
    | null
    | undefined
): boolean {
  if (!booking) return false;
  if (isBookingContractSigned(booking)) return true;
  if (booking.manual_contract_upload === true) return true;
  if (booking.signed_contract_url && String(booking.signed_contract_url).trim())
    return true;
  return false;
}

export function resolveLeasePipelineStatus(
  booking: any,
  depositPaidForBooking: boolean
): LeasePipelineStatus {
  // Admin-archived bookings are done deals — always read as archived,
  // regardless of how far they got in the signing/deposit pipeline.
  if (booking?.admin_archived_at) return "archived";
  // Explicit lifecycle statuses override the pipeline (see LEASE_STATUS_OVERRIDES).
  const override =
    LEASE_STATUS_OVERRIDES[String(booking?.status ?? "").toLowerCase()];
  if (override) return override;
  // Pre-sales deals only: reflect real contract + deposit progress.
  if (isBookingContractSigned(booking) && depositPaidForBooking)
    return "active";
  if (isBookingContractSigned(booking)) return "deposit_pending";
  return "signature_pending";
}
